import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { categories } from "../data/category-mapping";
import { ArrowRight } from "lucide-react";
import GlareHover from "../../components/GlareHover.jsx";

interface BentoLayout {
  colSpan: string;
  rowSpan: string;
  colStart?: string;
  rowStart?: string;
}

const bentoLayouts: BentoLayout[] = [
  { colSpan: "lg:col-span-1", rowSpan: "lg:row-span-2" },
  { colSpan: "lg:col-span-1", rowSpan: "lg:row-span-2" },
  { colSpan: "lg:col-span-2", rowSpan: "" },
  { colSpan: "lg:col-span-1", rowSpan: "" },
  { colSpan: "lg:col-span-1", rowSpan: "" },
  { colSpan: "lg:col-span-2", rowSpan: "" },
];

export default function MainCategories() {
  const navigate = useNavigate();

  return (
    <section className="py-16 md:py-24 bg-transparent relative z-10">
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="mb-10 md:mb-14">
          <div className="font-Montserrat text-xs tracking-widest uppercase text-indigo-600 dark:text-indigo-300/70 mb-4 flex items-center gap-4">
            <div className="w-4 h-px bg-indigo-500/50" />
            <span>Capabilities</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-Montserrat font-bold tracking-tight text-black dark:text-white">
            Our{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-black dark:from-white via-indigo-600 dark:via-indigo-200 to-indigo-500/50 italic font-serif">
              Expertise
            </span>
          </h2>
        </div>

        <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {categories.map((cat, i) => {
            const layout = bentoLayouts[i];
            const isWide = layout.colSpan.includes("col-span-2");

            return (
              <motion.div
                key={cat.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => navigate(`/technology/category/${cat.slug}`)}
                className={`${layout.colSpan} ${layout.rowSpan}`}
              >
                <GlareHover
                  width="100%"
                  height="100%"
                  borderRadius="1.25rem"
                  borderColor="rgba(120,120,120,0.12)"
                  glareColor="#ffffff"
                  glareOpacity={0.08}
                  glareSize={200}
                  glareAngle={-45}
                  transitionDuration={650}
                  className="group transition-all duration-300 hover:border-indigo-500/30"
                  style={{
                    background: "rgba(128,128,128,0.03)",
                    cursor: "pointer",
                    padding: "1.5rem",
                    placeItems: "stretch",
                  }}
                >
                  <div className="flex flex-col gap-3 h-full">
                    <div className="flex items-center justify-between">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5 text-black/70 dark:text-white/70 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 group-hover:scale-105 transition-all duration-300">
                        <cat.icon size={18} />
                      </div>
                      <div className="w-7 h-7 rounded-full flex items-center justify-center bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-300">
                        <ArrowRight size={12} className="text-black/60 dark:text-white/60" />
                      </div>
                    </div>

                    <div
                      className={`flex flex-col ${isWide ? "lg:flex-row lg:items-center lg:gap-6" : "gap-1.5"}`}
                    >
                      <div className={isWide ? "flex-1" : ""}>
                        <h3 className="text-base font-Montserrat font-semibold text-black dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors duration-300">
                          {cat.title}
                        </h3>
                        <p className="text-sm font-Montserrat text-black/60 dark:text-white/60 leading-relaxed mt-1">
                          {cat.description}
                        </p>
                      </div>
                      <div
                        className={`flex items-center gap-1.5 text-xs font-Montserrat font-semibold text-indigo-600/70 dark:text-indigo-400/70 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-all duration-300 ${isWide ? "mt-3 lg:mt-0 lg:shrink-0" : "pt-2"}`}
                      >
                        <span>View work</span>
                        <ArrowRight
                          size={12}
                          className="group-hover:translate-x-1 transition-transform duration-300"
                        />
                      </div>
                    </div>
                  </div>
                </GlareHover>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
